import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery, useTheme, Box } from "@mui/material";

import {
  Group as PanelGroup,
  Panel,
  Separator as PanelResizeHandle,
} from "react-resizable-panels";
import { useQuestion } from "../../../context/QuestionsContext";

import Navbar from "./Navbar";
import ProblemDescription from "./ProblemDescription";
import CodeEditor from "./CodeEditor";
import ConsoleTabs from "./ConsoleTabs";

// ─── Custom vertical drag divider ───────────────────────────────────────────
const CONSOLE_MIN_PX = 80; // minimum console height in px
const EDITOR_MIN_PX = 80; // minimum editor height in px

const languageValues = {
  C: "c",
  "C++": "cpp",
  "Java Script": "javascript",
  Python: "python",
  Java: "java",
  Ruby: "ruby",
};

const VerticalDivider = ({ onDrag }) => {
  const dragging = useRef(false);

  const onMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      dragging.current = true;

      const onMouseMove = (mv) => {
        if (dragging.current) onDrag(mv.clientY);
      };
      const onMouseUp = () => {
        dragging.current = false;
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [onDrag],
  );

  return (
    <div
      className="resize-handle-vertical"
      onMouseDown={onMouseDown}
      style={{ flexShrink: 0, userSelect: "none" }}
    />
  );
};

// ─── Right column: Editor (top) + draggable divider + Console (bottom) ───────
const EditorConsolePanel = ({ question }) => {
  const containerRef = useRef(null);
  // consoleHeight in px; null means "use default %"
  const [consoleHeight, setConsoleHeight] = useState(null);

  const handleDrag = useCallback((clientY) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const totalH = rect.height;
    const newConsoleH = rect.bottom - clientY;

    if (newConsoleH < CONSOLE_MIN_PX || totalH - newConsoleH < EDITOR_MIN_PX)
      return;

    setConsoleHeight(newConsoleH);
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Editor — fills remaining space above console */}
      <Box
        sx={{
          flexGrow: consoleHeight === null ? 65 : 1,
          flexShrink: 1,
          overflow: "hidden",
          ...(consoleHeight !== null && {
            // when user has dragged, let the editor take "the rest"
            height: `calc(100% - ${consoleHeight}px - 5px)`,
            flexGrow: 0,
            flexShrink: 0,
          }),
        }}
      >
        <CodeEditor question={question} languageValues={languageValues} />
      </Box>

      {/* Draggable divider */}
      <VerticalDivider onDrag={handleDrag} />

      {/* Console — fixed height once dragged, otherwise 35% */}
      <Box
        sx={{
          flexShrink: 0,
          overflow: "hidden",
          ...(consoleHeight === null
            ? { flexGrow: 35, flexBasis: "35%" }
            : { height: `${consoleHeight}px` }),
        }}
      >
        <ConsoleTabs question={question} languageValues={languageValues} />
      </Box>
    </Box>
  );
};

// ─── Main workspace ──────────────────────────────────────────────────────────
const CodingWorkspace = ({ activeQuestion }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { question } = useQuestion();
  const currentQuestion = 1;

  const questionData =
    question?.subjects?.map((subject) => subject?.questions).flat() || [];

  useEffect(() => {
    const handler = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  useEffect(() => {
    console.log("activeQuestion", activeQuestion);
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f9f9f9",
        textTransform: "capitalize",
      }}
    >
      <Navbar question={activeQuestion} />

      <Box sx={{ flexGrow: 1, p: 1, overflow: "hidden" }}>
        <PanelGroup
          direction={isMobile ? "vertical" : "horizontal"}
          id="main-group"
        >
          <Panel defaultSize={40} minSize={20}>
            <ProblemDescription
              question={activeQuestion}
              currentQuestion={currentQuestion}
            />
          </Panel>

          <PanelResizeHandle className="resize-handle-horizontal" />

          <Panel defaultSize={60} minSize={20}>
            <EditorConsolePanel question={activeQuestion} />
          </Panel>
        </PanelGroup>
      </Box>
    </Box>
  );
};

export default CodingWorkspace;
